/**
 * PreTrip tab content config: one video + PDF flip books.
 * Update these values (or replace with API) to change what volunteers see.
 * slotIndex ties each card to a backend slot (uploaded PDFs are stored by slot).
 * Display order is the array order; slotIndex can differ so existing PDFs stay correct when reordering.
 */

export type PreTripVideoConfig = {
  title: string
  /** YouTube embed URL (e.g. https://www.youtube.com/embed/...), Vimeo, or direct video URL */
  url: string
}

export type PreTripFlipbookConfig = {
  title: string
  /** Backend slot for this card (0, 1, 2, …). Determines where uploads are stored and which data is shown. */
  slotIndex: number
  /** Image URL per page (e.g. from PDF conversion or pre-rendered assets) */
  pageImageUrls: string[]
  /** URL to the original PDF file — Group Leaders can download from the PreTrip tab */
  pdfDownloadUrl?: string
}

export type PreTripConfig = {
  video: PreTripVideoConfig
  flipbooks: PreTripFlipbookConfig[]
}

/** Most regions: PreTrip / Take It Home split by Individuals & Small Groups vs Large Groups. */
export const PRETRIP_FLIPBOOKS_EXPANDED: PreTripFlipbookConfig[] = [
  { title: 'What to Bring & Expect', slotIndex: 1, pageImageUrls: [] },
  {
    title: 'PreTrip Training Individuals & Small Groups',
    slotIndex: 0,
    pageImageUrls: [],
  },
  {
    title: 'PreTrip Training Large Groups',
    slotIndex: 4,
    pageImageUrls: [],
  },
  { title: 'Fundraising Guide', slotIndex: 2, pageImageUrls: [] },
  {
    title: 'Take It Home Study Individuals & Small Groups',
    slotIndex: 3,
    pageImageUrls: [],
  },
  {
    title: 'Take It Home Study Large Groups',
    slotIndex: 5,
    pageImageUrls: [],
  },
]

/**
 * NC Swannanoa Valley, Harlem Heights, and any slug added here: one PreTrip Training card
 * and one Take It Home Study card (slots 0 and 3). Slots 4–5 are not shown.
 */
export const PRETRIP_FLIPBOOKS_SIMPLE: PreTripFlipbookConfig[] = [
  { title: 'What to Bring & Expect', slotIndex: 1, pageImageUrls: [] },
  { title: 'PreTrip Training', slotIndex: 0, pageImageUrls: [] },
  { title: 'Fundraising Guide', slotIndex: 2, pageImageUrls: [] },
  { title: 'Take It Home Study', slotIndex: 3, pageImageUrls: [] },
]

const PRETRIP_SIMPLE_LAYOUT_REGION_SLUGS = new Set([
  'swannanoa-valley',
  'harlem-heights',
])

export function getPreTripFlipbooksForRegionSlug(
  regionSlug: string | null | undefined
): PreTripFlipbookConfig[] {
  const key = regionSlug?.trim().toLowerCase() ?? ''
  if (key && PRETRIP_SIMPLE_LAYOUT_REGION_SLUGS.has(key)) {
    return PRETRIP_FLIPBOOKS_SIMPLE
  }
  return PRETRIP_FLIPBOOKS_EXPANDED
}

/** Default config — video + expanded flipbooks; UI uses getPreTripFlipbooksForRegionSlug per region. */
export const PRETRIP_CONFIG: PreTripConfig = {
  video: {
    title: 'PreTrip Video',
    url: '', // e.g. 'https://www.youtube.com/embed/...' or Vimeo embed URL
  },
  flipbooks: PRETRIP_FLIPBOOKS_EXPANDED,
}
